import { useState, useMemo, useRef } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";

import { InboxOutlined } from "@ant-design/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  Row,
  Col,
  Typography,
  Steps,
  Grid,
  Button,
  Modal,
  App,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  UploadProps,
} from "antd";

import dayjs from "../../../config/dayjs";
import { fileStatus, fileStatusLabels } from "../../../constant/enum";
import { getUser } from "../../../services/auth";
import { formatDate } from "../../../utils/dateUtils";
import {
  getFileById,
  updateFileStatus,
  downloadFileFromS3,
} from "../api/fileAPI";
import { IFileStatusRequest, FileStatusRequestSchema } from "../type/file";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const { Dragger } = Upload;

const StatusFile = () => {
  const { message } = App.useApp();
  const user = getUser();
  const role = user.role;
  const { id } = useParams();
  const fileId = id;
  const screens = useBreakpoint();
  const [current, setCurrent] = useState(0);
  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
  const [confirmUpdateStatusLoading, setConfirmUpdateStatusLoading] =
    useState(false);
  const formRef = useRef<any>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileList, setFileList] = useState<any[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [searchStatusText, setSearchStatusText] = useState("");

  const {
    data: fileData,
    refetch: refetchFile,
    isLoading,
  } = useQuery({
    queryKey: ["file", fileId],
    queryFn: () => getFileById(fileId),
    enabled: !!fileId,
  });

  const statusHistory = useMemo(() => {
    if (!fileData?.statusHistory?.length) return null;
    return fileData.statusHistory[current];
  }, [fileData, current]);

  const statusList = useMemo(() => {
    setCurrent(fileData?.statusHistory?.length - 1);
    return (
      fileData?.statusHistory?.map((status: any) => ({
        status: fileStatusLabels[status.status],
        statusRemark: status.statusRemark,
        dateExecuted: status.dateExecuted,
      })) || []
    );
  }, [fileData]);

  const onChange = (value: number) => {
    setCurrent(value);
  };

  // Responsive column spans based on screen size
  const statusColSpan = useMemo(() => {
    if (screens.xl) return 6;
    if (screens.lg) return 8;
    if (screens.md) return 24;
    return 24;
  }, [screens]);

  const detailsColSpan = useMemo(() => {
    if (screens.xl) return 18;
    if (screens.lg) return 16;
    if (screens.md) return 24;
    return 24;
  }, [screens]);

  /* ================ Update Status ================ */
  const showModal = () => {
    setIsUpdateStatusModalOpen(true);
    // Reset form, uploaded file and file list when opening modal
    reset({
      status: undefined,
      statusRemark: undefined,
      dateExecuted: undefined,
    });
    setUploadedFile(null);
    setFileList([]);
  };

  const statusOptions = useMemo(() => {
    // Filter out statuses that have already been used
    const usedStatuses = new Set(
      fileData?.statusHistory?.map((status: any) => status.status) || []
    );

    return fileStatus
      .filter((status) => {
        // Allow "others" to be submitted multiple times
        if (status === "others") {
          return true;
        }
        // Filter out other statuses that have already been used
        return !usedStatuses.has(status);
      })
      .map((type) => ({
        value: type,
        label: fileStatusLabels[type],
      }));
  }, [fileData?.statusHistory]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<IFileStatusRequest>({
    resolver: zodResolver(FileStatusRequestSchema),
    defaultValues: {
      status: undefined,
      statusRemark: undefined,
      dateExecuted: undefined,
    },
  });

  // Watch for changes in the status field
  const selectedStatus = watch("status");

  // Check if the date picker should be shown
  const showDatePicker =
    selectedStatus !== "uploadTitleSearch" &&
    selectedStatus !== "uploadDulyExecutedSpa";

  const showFileUpload =
    selectedStatus === "uploadTitleSearch" ||
    selectedStatus === "dulyStampedSpa" ||
    selectedStatus === "uploadDulyExecutedSpa" ||
    selectedStatus === "dulyStampedLetterOffer" ||
    selectedStatus === "requestRedemptionStatement" ||
    selectedStatus === "acknowledgementOfUtilitiesBill" ||
    selectedStatus === "vendorHandoverKeys" ||
    selectedStatus === "acknowledgementChangeOfNameUtilities" ||
    selectedStatus === "dulyRegisteredTitle";

  const props: UploadProps = {
    name: "file",
    multiple: false,
    fileList: fileList,
    beforeUpload: (file) => {
      // limit file size to 50MB
      if (file.size > 50 * 1024 * 1024) {
        message.error("File must be smaller than 50MB!");
        return Upload.LIST_IGNORE;
      }
      setUploadedFile(file);
      setFileList([
        {
          uid: "-1",
          name: file.name,
          status: "done",
          url: URL.createObjectURL(file),
        },
      ]);
      return false; // Prevent automatic upload
    },
    onRemove: () => {
      setUploadedFile(null);
      setFileList([]);
    },
    accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png",
  };

  const onSubmit = async (data: IFileStatusRequest) => {
    if (!fileData) return;

    try {
      setConfirmUpdateStatusLoading(true);

      // Validate file upload for statuses that require it
      if (showFileUpload && !uploadedFile) {
        message.error("Please upload a file for this status");
        setConfirmUpdateStatusLoading(false);
        return;
      }

      // Use the updated updateFileStatus function with the file parameter
      await updateFileStatus(fileData.id, data, uploadedFile || undefined);

      // Refresh the file data using refetch
      await refetchFile();

      // Reset form and file list
      reset({
        status: undefined,
        statusRemark: undefined,
        dateExecuted: undefined,
      });
      setUploadedFile(null);
      setFileList([]);

      message.success("File status updated successfully");
      setIsUpdateStatusModalOpen(false);
    } catch (error: any) {
      const errorMessage = error.message || "Failed to update file status";
      message.error(errorMessage);
    } finally {
      setConfirmUpdateStatusLoading(false);
    }
  };

  const handleCancel = () => {
    setIsUpdateStatusModalOpen(false);
    reset({
      status: undefined,
      statusRemark: undefined,
      dateExecuted: undefined,
    });
    setUploadedFile(null);
    setFileList([]);
  };

  /* ================ End of Update Status ================ */

  if (!fileId) {
    return <Navigate to="/404" replace />;
  }

  const handleFileDownload = async (fileUrl: string) => {
    try {
      setIsDownloading(true);
      await downloadFileFromS3(fileId, fileUrl);
      message.success("File downloaded successfully");
    } catch (error) {
      message.error("Failed to download file");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Row
        gutter={[16, 16]}
        style={{
          margin: screens.md ? 20 : 10,
          padding: screens.md ? 0 : "0 10px",
        }}
      >
        <Col span={statusColSpan}>
          <Card>
            <Title
              level={3}
              style={{
                textTransform: "uppercase",
                marginBottom: "1em",
                fontSize: screens.md ? 24 : 20,
              }}
            >
              File Status
            </Title>
            <Steps
              current={current}
              onChange={onChange}
              direction="vertical"
              progressDot
              size={screens.md ? "default" : "small"}
              style={{
                maxHeight: screens.md ? "none" : "300px",
                overflowY: screens.md ? "visible" : "auto",
              }}
              items={
                statusList?.map((status: any, index: number) => ({
                  title: (
                    <Text
                      style={{
                        fontSize: screens.md ? 16 : 14,
                        wordBreak: "break-word",
                      }}
                    >
                      {status.status}
                    </Text>
                  ),
                  status: current >= index ? "process" : "wait",
                })) || []
              }
            />
          </Card>
        </Col>
        {!isLoading && statusHistory && (
          <Col span={detailsColSpan}>
            <Card>
              <Title
                level={3}
                style={{
                  textTransform: "uppercase",
                  marginBottom: "1em",
                  fontSize: screens.md ? 24 : 20,
                }}
              >
                {fileStatusLabels[statusHistory.status]}
              </Title>
              <Text
                style={{
                  fontSize: screens.md ? 16 : 14,
                  display: "block",
                  wordBreak: "break-word",
                }}
              >
                {statusHistory.statusRemark}
              </Text>

              {statusHistory.fileURL && (
                <Button
                  type="link"
                  onClick={() => handleFileDownload(statusHistory.fileURL)}
                  style={{
                    fontSize: screens.md ? 16 : 14,
                    display: "block",
                    marginTop: 20,
                    padding: 0,
                  }}
                  loading={isDownloading}
                >
                  Download File
                </Button>
              )}

              <Text
                style={{
                  fontSize: screens.md ? 16 : 14,
                  display: "block",
                  wordBreak: "break-word",
                  marginTop: 20,
                }}
              >
                {formatDate(statusHistory.dateExecuted)}
              </Text>
            </Card>
            {role !== "client" && role !== "agent" && (
              <Button
                type="primary"
                style={{
                  marginTop: 10,
                  display: "block",
                  marginLeft: "auto",
                }}
                onClick={showModal}
                disabled={fileData.currentStatus.status === "completed"}
              >
                Update Status
              </Button>
            )}
          </Col>
        )}
      </Row>

      <Modal
        title="Update Status"
        open={isUpdateStatusModalOpen}
        onOk={handleSubmit(onSubmit)}
        onCancel={handleCancel}
        confirmLoading={confirmUpdateStatusLoading}
      >
        <Form ref={formRef} layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <Form.Item
            label="Status"
            name="status"
            hasFeedback
            validateStatus={errors.status ? "error" : ""}
            help={errors.status?.message}
            required
          >
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Select
                  placeholder="Select Status"
                  {...field}
                  size="large"
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onSearch={(value) => {
                    setSearchStatusText(value);
                  }}
                  options={statusOptions}
                  allowClear
                  notFoundContent={
                    searchStatusText
                      ? "No status found"
                      : statusOptions.length === 0
                      ? "No status available"
                      : null
                  }
                />
              )}
            />
          </Form.Item>
          <Form.Item
            label="Status Remark"
            name="statusRemark"
            hasFeedback
            validateStatus={errors.statusRemark ? "error" : ""}
            help={errors.statusRemark?.message}
            required
          >
            <Controller
              name="statusRemark"
              control={control}
              render={({ field }) => (
                <Input.TextArea
                  placeholder="Enter status remark"
                  {...field}
                  size="large"
                  rows={4}
                />
              )}
            />
          </Form.Item>

          {showDatePicker && (
            <Form.Item
              label="Date"
              name="dateExecuted"
              hasFeedback
              validateStatus={errors.dateExecuted ? "error" : ""}
              help={errors.dateExecuted?.message}
            >
              <Controller
                name="dateExecuted"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    style={{ width: "100%" }}
                    size="large"
                    onChange={(date) => {
                      // Convert dayjs object to JavaScript Date object for Zod validation
                      field.onChange(date ? date.toDate() : null);
                    }}
                    value={field.value ? dayjs(field.value) : null}
                  />
                )}
              />
            </Form.Item>
          )}

          {showFileUpload && selectedStatus && (
            <Form.Item
              label="Upload Document"
              name="fileUpload"
              tooltip="Allowed file types: PDF, DOC, DOCX, JPG, JPEG, PNG"
            >
              <Dragger {...props}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag file to this area to upload
                </p>
                <p className="ant-upload-hint">
                  Support for single file upload. Allowed file types: PDF, DOC,
                  DOCX, JPG, JPEG, PNG.
                </p>
              </Dragger>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default StatusFile;
